import { ref, computed, onMounted } from '@vue/composition-api'
import store from '@/store'
import { currency, formatDateBr } from '@core/utils/filter'
import { getUserData } from '@/auth/utils'

export default function useDashboardVendedor() {
  // Estados reativos
  const pedidos = ref([])
  const estatisticas = ref({
    totalPedidos: 0,
    totalVendas: 0,
    totalPendente: 0,
    totalCancelado: 0,
  })
  const loading = ref(false)
  const chartDataVendas = ref({
    labels: [],
    datasets: [{
      label: 'Vendas',
      data: [],
      backgroundColor: '#4F7DF3',
      borderColor: '#4F7DF3',
      borderWidth: 0,
      borderRadius: 5,
    }],
  })
  const chartDataPedidos = ref({
    labels: [],
    datasets: [{
      label: 'Pedidos',
      data: [],
      backgroundColor: 'transparent',
      borderColor: '#28C76F',
      pointBackgroundColor: '#28C76F',
      pointBorderColor: '#fff',
      pointRadius: 4,
      tension: 0.4,
      fill: false,
    }],
  })

  // Funções para resolver status dos pedidos
  const resolveStatusText = status => {
    if (status === 'Canceled') return 'Cancelado'
    if (status === 'Failed') return 'Falhou'
    if (status === 'Waiting_Payment') return 'Aguardando pagamento'
    if (status === 'Paid') return 'Pago'
    if (status === 'Completed') return 'Concluído'
    if (status === 'Expired') return 'Expirado'
    return 'Pendente'
  }

  const resolveStatusVariant = status => {
    if (status === 'Canceled') return 'danger'
    if (status === 'Failed') return 'info'
    if (status === 'Waiting_Payment') return 'warning'
    if (status === 'Paid') return 'success'
    if (status === 'Completed') return 'primary'
    if (status === 'Expired') return 'danger'
    return 'warning'
  }

  // Função para calcular total das comissões
  const calcularTotalComissoes = pedidoItens => {
    if (!pedidoItens || !Array.isArray(pedidoItens)) return 0
    return pedidoItens.reduce((totalPedido, item) => {
      if (!item.comissoes || !Array.isArray(item.comissoes)) return totalPedido
      const totalComissoesItem = item.comissoes.reduce((totalItem, comissao) => {
        const valorResto = comissao && comissao.resto ? Number(comissao.resto) : 0
        return totalItem + valorResto
      }, 0)
      return totalPedido + totalComissoesItem
    }, 0)
  }

  // Computados
  const dataStaticsFixos = computed(() => [
    {
      title: 'Pedidos',
      value: estatisticas.value.totalPedidos.toString(),
      icon: 'FileTextIcon',
      color: 'primary',
    },
    {
      title: 'Total Vendas',
      value: currency(estatisticas.value.totalVendas),
      icon: 'DollarSignIcon',
      color: 'success',
    },
    {
      title: 'Total Pendente',
      value: currency(estatisticas.value.totalPendente),
      icon: 'PackageIcon',
      color: 'warning',
    },
    {
      title: 'Total Cancelado',
      value: currency(estatisticas.value.totalCancelado),
      icon: 'XCircleIcon',
      color: 'danger',
    },
  ])

  const ultimosPedidos = computed(() => 
    pedidos.value.slice(0, 6).map(pedido => ({
      id: `#PED-${pedido.id}`,
      cliente: pedido.cliente?.nome || 'N/A',
      data: formatDateBr(pedido.data_venda),
      valor: currency(calcularTotalComissoes(pedido.pedido_itens)),
      status: {
        text: resolveStatusText(pedido.status),
        variant: resolveStatusVariant(pedido.status),
      },
    })),
  )

  const resumoRapido = computed(() => {
    const totalPedidos = pedidos.value.length
    const pedidosPagos = pedidos.value.filter(p => p.status === 'Paid').length
    const taxaConversao = totalPedidos > 0 ? ((pedidosPagos / totalPedidos) * 100).toFixed(1) : 0
    const clientesUnicos = new Set(pedidos.value.map(p => p.cliente?.id)).size
    const pedidosHoje = pedidos.value.filter(p => {
      const hoje = new Date().toDateString()
      const dataPedido = new Date(p.data_venda).toDateString()
      return hoje === dataPedido
    }).length

    return [
      { label: 'Taxa de Conversão', value: `${taxaConversao}%`, icon: 'TrendingUpIcon', color: 'success' },
      { label: 'Clientes Únicos', value: clientesUnicos.toString(), icon: 'UsersIcon', color: 'primary' },
      { label: 'Pedidos Hoje', value: pedidosHoje.toString(), icon: 'ShoppingCartIcon', color: 'info' },
      { label: 'Pedidos Cancelados', value: pedidos.value.filter(p => p.status === 'Canceled').length.toString(), icon: 'XCircleIcon', color: 'danger' },
    ]
  })

  // Função para buscar dados dos pedidos
  const fetchPedidos = async () => {
    try {
      loading.value = true
      const userData = getUserData()
      const idVendedor = userData?.vendedor?.id

      if (!idVendedor) {
        throw new Error('ID do vendedor não encontrado')
      }

      const response = await store.dispatch('appClientPedidos/findAll', {
        q: '',
        perPage: 100, // Buscar mais pedidos para estatísticas
        page: 1,
        sortBy: 'id',
        sortDesc: true,
      })

      pedidos.value = response.data.data || []
      calcularEstatisticas()
      gerarDadosGraficos()
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error)
    } finally {
      loading.value = false
    }
  }

  // Função para calcular estatísticas
  const calcularEstatisticas = () => {
    const totalPedidos = pedidos.value.length
    let totalVendas = 0
    let totalPendente = 0
    let totalCancelado = 0

    pedidos.value.forEach(pedido => {
      const valorComissoes = calcularTotalComissoes(pedido.pedido_itens)
      
      if (pedido.status === 'Paid' || pedido.status === 'Completed') {
        totalVendas += valorComissoes
      } else if (pedido.status === 'Waiting_Payment' || pedido.status === 'Pending') {
        totalPendente += valorComissoes
      } else if (pedido.status === 'Canceled') {
        totalCancelado += valorComissoes
      }
    })

    estatisticas.value = {
      totalPedidos,
      totalVendas,
      totalPendente,
      totalCancelado
    }
  }

  // Função para gerar dados dos gráficos
  const gerarDadosGraficos = () => {
    // Dados mensais (últimos 4 meses)
    const mesesLabels = []
    const vendasMensais = []
    const hoje = new Date()
    for (let i = 3; i >= 0; i--) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)
      const mesLabel = data.toLocaleDateString('pt-BR', { month: 'short' })
      mesesLabels.push(mesLabel.charAt(0).toUpperCase() + mesLabel.slice(1))
      const pedidosDoMes = pedidos.value.filter(pedido => {
        const dataPedido = new Date(pedido.data_venda)
        return dataPedido.getMonth() === data.getMonth() && 
               dataPedido.getFullYear() === data.getFullYear() &&
               (pedido.status === 'Paid' || pedido.status === 'Completed')
      })
      const vendaDoMes = pedidosDoMes.reduce((total, pedido) => 
        total + calcularTotalComissoes(pedido.pedido_itens), 0)
      vendasMensais.push(vendaDoMes)
    }

    chartDataVendas.value = {
      labels: mesesLabels,
      datasets: [{
        label: 'Vendas',
        data: vendasMensais,
        backgroundColor: '#4F7DF3',
        borderColor: '#4F7DF3',
        borderWidth: 0,
        borderRadius: 5,
      }]
    }

    // Dados semanais (últimos 7 dias)
    const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
    const pedidosPorDia = []
    
    for (let i = 6; i >= 0; i--) {
      const data = new Date()
      data.setDate(data.getDate() - i)
      
      const pedidosDoDia = pedidos.value.filter(pedido => {
        const dataPedido = new Date(pedido.data_venda).toDateString()
        return dataPedido === data.toDateString()
      }).length
      
      pedidosPorDia.push(pedidosDoDia)
    }

    const ultimosSeteLabel = []
    for (let i = 6; i >= 0; i--) {
      const data = new Date()
      data.setDate(data.getDate() - i)
      ultimosSeteLabel.push(diasSemana[data.getDay()])
    }

    chartDataPedidos.value = {
      labels: ultimosSeteLabel,
      datasets: [{
        label: 'Pedidos',
        data: pedidosPorDia,
        backgroundColor: 'transparent',
        borderColor: '#28C76F',
        pointBackgroundColor: '#28C76F',
        pointBorderColor: '#fff',
        pointRadius: 4,
        tension: 0.4,
        fill: false,
      }]
    }
  }

  // Lifecycle
  onMounted(() => {
    fetchPedidos()
  })

  return {
    // Estados
    pedidos,
    loading,
    estatisticas,
    
    // Computados
    dataStaticsFixos,
    ultimosPedidos,
    resumoRapido,
    chartDataVendas,
    chartDataPedidos,
    
    // Métodos
    fetchPedidos,
    calcularTotalComissoes,
    resolveStatusText,
    resolveStatusVariant,
  }
}
